import axios from 'axios'

// The owner's mobile for urgent texts: the one phone a wake texts (BRIEF-A2c,
// D12). Mounted unprefixed at /auth/v2/* (no /api/v1), like analystLogin.
// The status never carries the full number, only its last four digits.
// The base URL is read here, as api.ts does, so a test that mocks api.ts
// does not have to mock this too.
const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'

export interface OwnerPhoneStatus {
  verified: boolean
  last_four: string | null
  stopped: boolean
}

function headers() {
  const token = localStorage.getItem('dw_token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

export async function getOwnerPhone(): Promise<OwnerPhoneStatus> {
  const response = await axios.get<OwnerPhoneStatus>(`${API_BASE_URL}/auth/v2/phone/owner`, {
    headers: headers(),
    timeout: 30000,
  })
  return response.data
}

export async function requestOwnerPhoneCode(phoneNumber: string): Promise<void> {
  await axios.post(
    `${API_BASE_URL}/auth/v2/phone/owner/request-code`,
    { phone_number: phoneNumber },
    { headers: headers(), timeout: 30000 },
  )
}

export async function verifyOwnerPhone(phoneNumber: string, code: string): Promise<void> {
  await axios.post(
    `${API_BASE_URL}/auth/v2/phone/owner/verify`,
    { phone_number: phoneNumber, code },
    { headers: headers(), timeout: 30000 },
  )
}

function status(err: unknown): number | undefined {
  return axios.isAxiosError(err) ? err.response?.status : undefined
}

function detail(err: unknown): string {
  if (!axios.isAxiosError(err)) return ''
  const d = (err.response?.data as { detail?: unknown } | undefined)?.detail
  return typeof d === 'string' ? d : ''
}

// Plain words for each refusal of the request-code route.
export function requestCodeError(err: unknown): string {
  switch (status(err)) {
    case 422:
      return 'That is not a mobile number. Try the full number, for example +64 21 123 4567.'
    case 429:
      return /today/i.test(detail(err))
        ? 'We have sent too many codes today. Please try again tomorrow.'
        : 'We have sent too many codes to this number. Please try again in an hour.'
    case 403:
      return 'Only the business owner can set this mobile.'
    default:
      return 'We could not send the code just now. Please try again.'
  }
}

// Plain words for each refusal of the verify route.
export function verifyCodeError(err: unknown): string {
  switch (status(err)) {
    case 401:
      return 'That code is not right. Check the text we sent and try again.'
    case 429:
      return 'Too many wrong codes. Ask for a new code.'
    case 410:
      return 'That code has expired. Ask for a new code.'
    case 404:
      return 'Ask for a code for this number first.'
    case 422:
      return 'That is not a mobile number.'
    case 403:
      return 'Only the business owner can set this mobile.'
    default:
      return 'We could not check the code just now. Please try again.'
  }
}
